import React, { FC, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { APP_VERSION_PLATFORMS } from "@/domain/app-versions.interface";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  platform: string;
  isCurrent: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  backoffice: "Backoffice",
};

const AppVersionFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;

  const platformOptions = useMemo(
    () =>
      APP_VERSION_PLATFORMS.map((p) => ({
        key: p,
        value: PLATFORM_LABELS[p] || p,
      })),
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.surface.light2,
          px: 2,
          width: {
            xs: "100%",
            sm: "100%",
          },
        }}
        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
      >
        <InputBase
          className="flex-1"
          placeholder="Search by version"
          inputProps={{ "aria-label": "search" }}
          onChange={(e) => {
            setFilters((filters) => ({
              ...filters,
              search: e.target.value,
              pageNumber: 0,
            }));
          }}
          value={filters.search}
        />
        <SearchIcon className="text-icon-primary" />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: {
            xs: "100%",
            sm: "100%",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
            md: "row",
          },
        }}
      >
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              platform: value as string,
              pageNumber: 0,
            }));
          }}
          data={platformOptions}
          value={filters.platform}
          label="Platform"
          id="app-version-platform-filter"
          maxWidth={200}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              isCurrent: value as string,
              pageNumber: 0,
            }));
          }}
          data={[
            { key: "true", value: "Current" },
            { key: "false", value: "Not current" },
          ]}
          value={filters.isCurrent}
          label="Current"
          id="app-version-current-filter"
          maxWidth={200}
          showClearOption
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default AppVersionFilters;
